/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';

import MainSection from 'types/MainSection';
import { ActionButton, AssetActionButton } from 'types/components/ActionButtonsGroup';

import { BackgroundVideo, Badge } from '@wit/vtv-x-foundation-lib';
import HomeHero from './HomeHero';
import SectionHero from './SectionHero';
import Overlay from './Overlay';
import { ImageLoader } from '..';

interface Props {
  className?: string;
  section?: MainSection;
  toAuthenticate?: boolean;
  logo?: string;
  backgroundImage?: string;
  backgroundVideo?: BackgroundVideo;
  onClick?: () => void;
  backLabel?: string;
  backUrl?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  placeholder?: string;
  badges?: Badge[];
  actionButtons?: (ActionButton | AssetActionButton)[];
}

interface AvailableSectionsMap {
  [key: string]: MainSection;
}

const availableSectionsMap: AvailableSectionsMap = {
  [MainSection.MY_TV]: MainSection.MY_TV,
  [MainSection.WATCH_TV]: MainSection.WATCH_TV,
  [MainSection.ONDEMAND]: MainSection.ONDEMAND,
};

function heroFactory({
  className,
  section,
  toAuthenticate = false,
  onClick,
  backLabel,
  backUrl,
  title,
  subtitle,
  description,
  placeholder,
  badges,
  actionButtons,
}: Props): JSX.Element {
  if (toAuthenticate && section && actionButtons) {
    return (
      <HomeHero
        section={availableSectionsMap[section] ? section : MainSection.WATCH_TV}
        actionButtons={actionButtons}
      />
    );
  }
  return (
    <SectionHero
      className={className}
      onClick={onClick}
      backLabel={backLabel}
      backUrl={backUrl}
      title={title}
      subtitle={subtitle}
      description={description}
      placeholder={placeholder}
      badges={badges}
      actionButtons={actionButtons}
    />
  );
}

function Hero({
  className,
  section,
  backgroundImage,
  backgroundVideo,
  toAuthenticate,
  logo,
  onClick,
  backLabel,
  backUrl,
  title,
  subtitle,
  description,
  placeholder,
  badges,
  actionButtons,
}: Props): JSX.Element {
  return (
    <>
      <div className={`${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
        <Overlay backgroundImage={backgroundImage} backgroundVideo={backgroundVideo} />
      </div>
      {logo ? <ImageLoader className="hero-logo-container" src={logo} bemModifier="heroLogo" /> : null}
      {heroFactory({
        className,
        section,
        toAuthenticate,
        onClick,
        backLabel,
        backUrl,
        title,
        subtitle,
        description,
        placeholder,
        badges,
        actionButtons,
      })}
    </>
  );
}

export default React.memo(Hero);
